import React, { useCallback, useEffect, useMemo } from 'react';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { Form, FormInstance, notification, Popconfirm, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import ButtonOperation from 'components/ButtonOperation';
import {
  createThirdPartyAddress,
  deleteThirdPartyAddress,
  getThirdPartyAddresListByTpid,
  TpAddress,
  updateThirdPartyAddres,
} from 'services/thirdParty';
import DeleteButton from 'components/DeleteButton';
import ModalController from 'components/ModalController';
import AllAddressesModal from './AllAddressesModal';
import Text from 'antd/lib/typography/Text';
import ThirdPartyAddresStatusButton from './ThirdPartyAddresStatusButton';
import { indexBy } from 'utils/utils2';
type AddressProps = {
  form: FormInstance;
  tpid: number;
  onClose?: any;
  disabled?: boolean;
  showAsModal?: boolean;
};

const AdressesList = (props: AddressProps) => {
  const { form, tpid, showAsModal } = props;
  const fieldName = 'allAdressesComponents';

  const { data: Adresses, refetch } = useQuery(
    ['getThirdPartyAddresListAction', { tpid: Number(tpid) }],
    () =>
      getThirdPartyAddresListByTpid({ tpid: Number(tpid) }).then(
        (res) => res.list || []
      ),
    { enabled: !!tpid }
  );

  
  useEffect(() => {
    if (Adresses) {
      form.setFieldsValue({ [fieldName]: Adresses });
      refetch();
    } else {
      form.setFieldsValue({ [fieldName]: [] });
    }
  }, [form, Adresses, refetch]);

  const onSave = useCallback(
    (record: any, action?: string) => {
      action === 'create'
        ? createThirdPartyAddress({
            tpAddressRequest: { ...record, tpid },
            tpid: tpid,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateThirdPartyAddres({
            tpAddressRequest: record,
            tpadrid: Number(record.tpadrid),
            tpid: tpid,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [refetch, tpid]
  );
  const getName = useCallback(
    (name: string) => (record: TpAddress) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions ?? [], 'tpadrid')[record?.tpadrid];

      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<TpAddress>[]>(
    () => [
      {
        dataIndex: 'adrtypelabel',
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'tpmainadrstreet1',
        render: (_value, _record, index) => (
          <ModalController>
            {({ setVisible, visible }) => (
              <>
                <a onClick={() => setVisible(true)}>
                  {_record.tpmainadrstreet1}
                </a>
                {_record.adrstatus !== 'END' && (
                  <AllAddressesModal
                    showAsModal={showAsModal}
                    visible={visible}
                    onClose={() => {
                      setVisible(false);
                      refetch();
                    }}
                    onSave={(value) => {
                      onSave(value);
                      setVisible(false);
                    }}
                    tpid={tpid}
                    record={_record}
                    initadd={initialValue}
                  />
                )}
              </>
            )}
          </ModalController>
        ),
      },

      {
        dataIndex: 'tpmainadrstreet2',
        fixed: true,
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'tpmainadrcitylabel',
        fixed: true,
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'tpmainadrcountrylabel',
        fixed: true,
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'tpmainadrregionlabel',
        fixed: true,
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'tpmainadrzipcodelabel',
        fixed: true,
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'adrstatusdate',
        fixed: true,
        render: (_value, _record, index) => <Text>{_value}</Text>,
      },
      {
        dataIndex: 'adrstatus',
        fixed: true,
        render: (_value, _record, index) => (
          <span>
            {_record.adrstatus.split(',').map((tag) => {
              let color;

              if (tag === 'VALID') {
                color = 'green';
              }
              if (tag === 'DISABLED') {
                color = ' #ffcc00';
              }
              if (tag === 'END') {
                color = 'red';
              }

              return (
                <Tag color={color} key={tag}>
                  {_record.adrstatus}
                </Tag>
              );
            })}
          </span>
        ),
      },
      {
        dataIndex: 'actions',
        key: 'actions',
        width:  180,
        render: (_, _record) => (
          <>
            {localStorage.getItem('userrole') !== 'BROKER' && (
              <ThirdPartyAddresStatusButton
                tpid={_record.tpid}
                tpadrid={_record.tpadrid}
                status={_record.adrstatus}
                refetch={refetch}
              />
            )}
            {localStorage.getItem('userrole') === 'ADMIN' && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteThirdPartyAddress({
                    tpadrid: _record.tpadrid,
                    tpid: _record.tpid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton
                  onlyIcon
                  style={{ height: '40px', width: '40px' }}
                />
              </Popconfirm>
            )}
          </>
        ),
       
        fixed: 'right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSave, refetch, tpid, getName]
  );

  return (
    <Form form={form}>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          return (
            <DataGrid
              rowKey="tpadrid"
              dataSource={Adresses}
              columns={
                !showAsModal
                  ? columns
                  : columns.filter((cl) => cl.key !== 'actions')
              }
              hideFilters
              extraActions={
                <ModalController>
                  {({ setVisible, visible }) => (
                    <>
                      {!showAsModal && (
                        <ButtonOperation
                          type="primary"
                          icon={<PlusOutlined />}
                          comcode="addAdr"
                          onClick={(ev) => {
                            setVisible(true);
                            ev.stopPropagation();
                          }}
                        />
                      )}

                      <AllAddressesModal
                        visible={visible}
                        onClose={() => {
                          setVisible(false);
                          refetch();
                        }}
                        onSave={(value) => {
                          onSave(value, 'create');
                          setVisible(false);
                        }}
                        tpid={tpid}
                        initadd={initialValue}
                      />
                    </>
                  )}
                </ModalController>
              }
            />
          );
        }}
      </Form.Item>
    </Form>
  );
};
const initialValue = {
  adrenddate: '',
  adrstartdate: '',
  adrstatus: '',
  adrstatusdate: '',
  adrtype: '',
  tpadrid: 0,
  tpid: 0,
  tpmainadrcity: '',
  tpmainadrcountry: '',
  tpmainadrregion: '',
  tpmainadrregionglobale: '',
  tpmainadrstreet1: '',
  tpmainadrstreet2: '',
  tpmainadrzipcode: '',
};
export default AdressesList;
